import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { CheckboxOutlineIcon, CheckedIcon, DropdownIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  selectManufacturerModelForm,
} from "../../../redux/selector";
import { manufacturerModelActions } from "../../../redux/slice";
import { ManufacturerModelForm } from "../../../types";
import { useStyles } from "./styles";

const FIELDS: Array<{
  key: keyof ManufacturerModelForm;
  label: string;
  type: "select" | "text";
  wide?: boolean;
}> = [
  { key: "manufacturer", label: "MANUFACTURER", type: "select" },
  { key: "year", label: "YEAR", type: "select" },
  { key: "masterModel", label: "MASTER_MODEL", type: "select" },
  { key: "modelDescription", label: "MODEL_DESCRIPTION", type: "text" },
  { key: "licensedWeight", label: "LICENSED_WEIGHT", type: "text" },
  { key: "frontOverhang", label: "FRONT_OVERHANG", type: "text" },
  { key: "drive", label: "DRIVE", type: "select" },
  { key: "wheelbase", label: "WHEELBASE", type: "select" },
  { key: "frontAxleDistance", label: "FRONT_AXLE_DISTANCE", type: "text" },
  { key: "chassisWeight", label: "CHASSIS_WEIGHT", type: "text" },
  { key: "openingBase", label: "OPENING_BASE", type: "select" },
  { key: "chassisWidth", label: "CHASSIS_WIDTH", type: "text" },
  { key: "maxWidth", label: "MAX_WIDTH", type: "text", wide: true },
  {
    key: "registrationInstruction",
    label: "REGISTRATION_INSTRUCTION",
    type: "text",
    wide: true,
  },
];

const CHECKS: Array<{ key: keyof ManufacturerModelForm; label: string }> = [
  { key: "sideLighting", label: "SIDE_LIGHTING" },
  { key: "sideGuard", label: "SIDE_GUARD" },
  { key: "mudguard", label: "MUDGUARD" },
  { key: "extendableChassis", label: "EXTENDABLE_CHASSIS" },
];

const P = "MANUFACTURER_MODELS.MODAL";

const ModelDetailsForm = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const form = useSelector(selectManufacturerModelForm);

  const set = (name: keyof ManufacturerModelForm, value: string | boolean) =>
    dispatch(manufacturerModelActions.setFormField({ name, value }));

  const ph = (label: string, type: "select" | "text") =>
    t(`${P}.PLACEHOLDERS.${type === "select" ? "SELECT" : "ENTER"}_${label}`);

  return (
    <Box className={styles.root}>
      <Box className={styles.grid}>
        {FIELDS.map(({ key, label, type, wide }) => (
          <Box
            key={key}
            className={`${styles.field}${wide ? ` ${styles.fieldWide}` : ""}`}
          >
            <Typography className={styles.label}>
              {t(`${P}.FIELDS.${label}`)}
            </Typography>
            <TextField
              className={styles.input}
              fullWidth
              select={type === "select"}
              variant="outlined"
              placeholder={ph(label, type)}
              value={form[key] as string}
              InputProps={{ notched: false }}
              SelectProps={
                type === "select"
                  ? {
                      displayEmpty: true,
                      IconComponent: DropdownIcon,
                      renderValue: (v) =>
                        (v as string) || ph(label, "select"),
                    }
                  : undefined
              }
              onChange={(e) => set(key, e.target.value)}
            >
              {type === "select" ? (
                <MenuItem value="">{ph(label, "select")}</MenuItem>
              ) : undefined}
            </TextField>
          </Box>
        ))}
      </Box>

      <Box className={styles.checks}>
        {CHECKS.map(({ key, label }) => (
          <FormControlLabel
            key={key}
            className={styles.checkCard}
            labelPlacement="end"
            control={
              <Checkbox
                checked={form[key] as boolean}
                onChange={(e) => set(key, e.target.checked)}
                icon={<CheckboxOutlineIcon width={24} height={24} />}
                checkedIcon={<CheckedIcon width={24} height={24} />}
              />
            }
            label={t(`MANUFACTURER_MODELS.TABLE.${label}`)}
          />
        ))}
      </Box>

      <Box className={styles.notesField}>
        <Typography className={styles.label}>{t(`${P}.FIELDS.NOTES`)}</Typography>
        <TextField
          className={styles.notesInput}
          fullWidth
          multiline
          variant="outlined"
          placeholder={t(`${P}.PLACEHOLDERS.ENTER_NOTES`)}
          value={form.notes}
          InputProps={{ notched: false }}
          onChange={(e) => set("notes", e.target.value)}
        />
      </Box>
    </Box>
  );
};

export default ModelDetailsForm;
